import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Card, Typography, Icon, Button, Avatar, Tag } from '@ann/web-components';
import { NameInitialsAvatar } from 'react-name-initials-avatar';
import { getAvatarUrl, generateAvatarColor } from '../../../../../../../utils/avatarUtils';
import { useToast } from '../../../../../../../components/toast';
import { 
    CardContent, 
    CardContentItem, 
    CardWrapper,
    CardHeader,
    CardHeaderLeft,
    CardHeaderRight,
    CardDetailsWrapper,
    WorkspaceAvatarWithInitials,
} from './WorkspaceCard.styled';

const WorkspaceCard = ({ workspace, onClick }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [imageFailed, setImageFailed] = useState(false);

  const workspaceId = workspace.workspaceUuid || workspace.tenant_uuid || workspace.tenant_id || workspace.id || workspace.tenantUuid || workspace.tenantId;
  // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
  const workspaceName = workspace.workspaceName || workspace.workspace_name || workspace.tenantName || workspace.tenant_name || workspace.name || workspace.tenant_name || 'Unnamed Workspace';
  const ownerName = workspace.contact_name || workspace.contactName || workspace.ownerName || workspace.owner_name || '';
  const createdAt = workspace.createdAt || workspace.created_at;
  const userCount = workspace.userCount ?? workspace.user_count ?? 0;
  const photoUrl = workspace.profilePhotoUrl || workspace.photo_url;
  
  const avatarUrl = imageFailed ? null : getAvatarUrl(photoUrl, workspaceName);
  const avatarColor = generateAvatarColor(workspaceName);
  const showInitialsAvatar = imageFailed || !avatarUrl;
    
  const handleImageError = useCallback(() => {
    setImageFailed(true);
  }, []);

  const getPlainWorkspaceData = () => {
    return {
      tenant_id: workspace.tenant_id || workspace.tenant_uuid,
      tenant_uuid: workspace.tenant_uuid,
      id: workspace.id,
      tenant_name: workspace.tenant_name,
      name: workspace.name,
      status: workspace.status,
      photo_url: workspace.photo_url,
      profilePhotoUrl: workspace.profilePhotoUrl,
      created_at: workspace.created_at,
      createdAt: workspace.createdAt,
      user_count: workspace.user_count,
      userCount: workspace.userCount,
      contact_name: workspace.contact_name,
      contactName: workspace.contactName,
      owner_name: workspace.owner_name,
      ownerName: workspace.ownerName,
    };
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(workspaceId);
    } else if (workspaceId) {
      navigate(`/v2/workspace/${workspaceId}`, {
        state: { 
          workspaceName: workspaceName,
          workspaceData: getPlainWorkspaceData()
        }
      });
    }
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    const tenantUuid = workspace.tenant_uuid || workspace.tenantUuid || workspace.workspace_uuid || workspace.workspaceUuid;
    if (!tenantUuid) {
      toast.error({
        message: 'Unable to identify workspace',
        canClose: true
      });
      return;
    }
    if (workspaceId) {
      navigate(`/v2/workspace/${workspaceId}`, {
        state: { 
          workspaceName: workspaceName,
          workspaceData: getPlainWorkspaceData()
        }
      });
    }
  };

  const getStatusDisplay = (workspace) => {
    // Check isActive field first - if false, show Deactivated
    const isActive = workspace.isActive !== undefined ? workspace.isActive : workspace.is_active;
    if (isActive === false) {
      return { text: 'Deactivated', color: 'error' };
    }
    
    // Otherwise, show status from backend
    const status = workspace.status;
    switch (status?.toLowerCase()) {
      case 'ready':
      case 'active':
        return { text: 'Active', color: 'success' };
      case 'in_progress':
        return { text: 'In Progress', color: 'informative' };
      case 'inactive':
        return { text: 'Inactive', color: 'neutral' };
      default:
        return { text: status || 'Unknown', color: 'neutral' };
    }
  };

  const statusInfo = getStatusDisplay(workspace);

  return (
    <div>
      <Card onClick={handleCardClick}>
        <CardWrapper>
          <CardHeader>
            <CardHeaderLeft>
              {!showInitialsAvatar && avatarUrl ? (
                <Avatar 
                  alt={workspaceName} 
                  size="md" 
                  src={avatarUrl}
                  onError={handleImageError}
                />
              ) : (
                <WorkspaceAvatarWithInitials>
                  <NameInitialsAvatar
                    name={workspaceName}
                    bgColor={avatarColor}
                    textColor="#ffffff"
                    size="40px"
                    borderRadius="50%"
                    textWeight="bold"
                    textSize="14px"
                    borderWidth="0"
                  />
                </WorkspaceAvatarWithInitials>
              )}
              <Typography category="subtitle" size="small" weight="bold">
                {workspaceName}
              </Typography>
            </CardHeaderLeft>
            <CardHeaderRight>
              <Tag
                color={statusInfo.color}
                onClose={() => {}}
                size="medium"
                text={statusInfo.text}
               />
            </CardHeaderRight>
          </CardHeader>

           <CardContent>
            <CardContentItem>
                <Icon name="person" size="small"/>
                <Typography category="body" size="small" weight="regular" color="body">
                  {ownerName || '-'}
                </Typography>
            </CardContentItem>
            <CardContentItem>
                <Icon name="calendarToday" size="small"/>
                <Typography category="body" size="small" weight="regular" color="body">
                  {createdAt ? dayjs(createdAt).format('MMM D · YYYY') : 'N/A'}
                </Typography>
            </CardContentItem>
            <CardContentItem>
                <Icon name="group" size="small"/>
                <Typography category="body" size="small" weight="regular" color="body">
                 {workspace.user_count > 0 ? `+${workspace.user_count}` : '0'}
                </Typography>
            </CardContentItem>
           </CardContent>
           <CardDetailsWrapper>
             <Button label="View details" size="small" type="tertiary" disableEffects={true} onClick={handleViewDetails} />
           </CardDetailsWrapper>
        </CardWrapper>
      </Card>
    </div>
  );
};

export default WorkspaceCard;
